import { CalendarDays, ListTodo, PiggyBank, Settings } from "lucide-react";
import { de } from "@ainkauf/core";
import { useNavigate } from "@/lib/router";

/**
 * Die untere Leiste.
 *
 * Vorher war sie eine schwebende Pille in der Mitte. Das sah leicht aus, aber
 * es fühlte sich nach Webseite an: Sie schwamm über dem Inhalt, und auf dem
 * iPhone stand sie am Ende einer kurzen Liste irgendwo im Nichts. Jetzt sitzt
 * sie fest am unteren Rand, über die volle Breite, mit einer Haarlinie nach
 * oben und der Safe-Area als eigenem Polster. Das ist die Form, die jede
 * native App benutzt, und der Daumen findet sie blind.
 *
 * Alle Beschriftungen stehen immer da. Ein Symbol allein muss man raten,
 * und geraten wird in einer Einkaufsliste nicht.
 *
 * Seit Stufe 5 sind es vier Felder. Auf 390 Pixel sind das knapp 97 Pixel je
 * Feld, immer noch weit über den 44 Pixeln aus Regel 8. Ein fünftes gäbe es
 * nicht: Rezepte, Wochenplan und Vorrat teilen sich deshalb "Planen".
 */
export function BottomNav({ active }: { active: "listen" | "planen" | "ausgaben" | "einstellungen" }) {
  const go = useNavigate();

  const tabs = [
    { key: "listen" as const, label: de.nav.lists, icon: ListTodo, path: "/listen" },
    { key: "planen" as const, label: de.nav.plan, icon: CalendarDays, path: "/planen" },
    { key: "ausgaben" as const, label: de.nav.spending, icon: PiggyBank, path: "/ausgaben" },
    { key: "einstellungen" as const, label: de.nav.settings, icon: Settings, path: "/einstellungen" },
  ];

  return (
    <nav
      className="glas fixed inset-x-0 bottom-0 z-20 border-t border-[color-mix(in_oklab,var(--ink)_8%,transparent)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <div className="mx-auto flex w-full max-w-2xl">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const aktiv = tab.key === active;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => {
                go(tab.path);
              }}
              aria-current={aktiv ? "page" : undefined}
              data-testid={`nav-${tab.key}`}
              className="relative flex flex-1 flex-col items-center justify-center gap-1 py-2 transition-transform active:scale-95"
              style={{ minHeight: "var(--leiste)", color: aktiv ? "var(--accent)" : "var(--ink-3)" }}
            >
              {/* Kurzer Strich über dem gewählten Feld: der ruhigste Weg,
                  einen Zustand zu zeigen, ohne eine Fläche einzufärben. */}
              <span
                aria-hidden
                className="absolute inset-x-0 top-0 mx-auto h-[3px] rounded-full transition-all duration-200 [transition-timing-function:var(--ease-spring)]"
                style={{ width: aktiv ? "28px" : "0px", background: "var(--accent)" }}
              />
              <Icon size={21} strokeWidth={aktiv ? 2.25 : 1.75} aria-hidden />
              <span className={`text-[11px] leading-none ${aktiv ? "font-bold" : "font-semibold"}`}>{tab.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
